import { History, Trash2, UserCog } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import TicketEmailTimeline from "@/components/TicketEmailTimeline";
import KbSuggesterPanel from "@/components/kb/KbSuggesterPanel";

export const ALL_STATUSES = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "pending_user", label: "Pending User" },
  { value: "pending_department", label: "Pending Department Action" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

export const TicketSidebar = ({
  ticket, agents, user, priorityOptions,
  allowedStatuses, canSetStatus, canChangeStatus,
  canViewAssignee, canAssign, canDelete,
  onStatusChange, onUpdate, onDelete, onOpenHistory, onInsertKbLink,
}) => (
  <div className="space-y-4">
    <Card className="border-slate-200">
      <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Properties</CardTitle></CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Status</div>
          <Select value={ticket.status} onValueChange={onStatusChange} disabled={!canChangeStatus}>
            <SelectTrigger data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ALL_STATUSES.map((s) => {
                const transitionOk = s.value === ticket.status || allowedStatuses.includes(s.value);
                const allowed = transitionOk && (s.value === ticket.status || canSetStatus(s.value));
                return (
                  <SelectItem
                    key={s.value} value={s.value}
                    disabled={!allowed}
                    data-testid={`status-option-${s.value}`}
                  >
                    {s.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-400 mt-1">
            {canChangeStatus
              ? "Closing or cancelling will open a reason picker."
              : "You do not have permission to change status."}
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Priority</div>
          <Select
            value={ticket.priority}
            onValueChange={(v) => onUpdate({ priority: v })}
            disabled={!user?.is_super_admin}
          >
            <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {priorityOptions.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {!user?.is_super_admin && (
            <p className="text-[10px] text-slate-400 mt-1">Locked — super admin only after creation.</p>
          )}
        </div>
        {canViewAssignee && (
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
              <UserCog className="w-3 h-3" /> Assignee
            </div>
            <Select value={ticket.owner_email || ""} onValueChange={(v) => onUpdate({ owner_email: v })} disabled={!canAssign}>
              <SelectTrigger data-testid="ticket-owner-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.email} value={a.email}>
                    {a.name} <span className="text-slate-400 ml-1.5 text-xs">({a.role})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canAssign && (
              <p className="text-[10px] text-slate-400 mt-1">You do not have permission to assign tickets.</p>
            )}
          </div>
        )}
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Channel</div>
          <div className="text-slate-800">{ticket.channel}</div>
        </div>
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Category</div>
          <div className="text-slate-800">{ticket.category}{ticket.subcategory ? ` → ${ticket.subcategory}` : ""}</div>
        </div>
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</div>
          <div className="text-slate-800">{ticket.type}</div>
        </div>
        {ticket.department_name && (
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Department</div>
            <div className="text-slate-800">{ticket.department_name}</div>
          </div>
        )}
        {ticket.contact_number && (
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contact</div>
            <div className="text-slate-800 font-mono text-xs">{ticket.country_code} {ticket.contact_number}</div>
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenHistory}
              data-testid="td-view-customer-history-btn"
              className="mt-1 h-8 w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              <History className="w-3.5 h-3.5 mr-1.5" /> View Customer History
            </Button>
          </div>
        )}
      </CardContent>
    </Card>

    {canDelete && (
      <Button
        variant="outline"
        onClick={onDelete}
        className="w-full text-rose-600 border-rose-200 hover:bg-rose-50"
        data-testid="ticket-delete-btn"
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
        Delete ticket
      </Button>
    )}

    <TicketEmailTimeline ticketId={ticket.id} />

    {ticket.kb_suggested_at_intake && (
      <div
        className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 flex items-start gap-2"
        data-testid="kb-intake-badge"
        title="The intake webhook auto-matched these KB articles when this ticket arrived."
      >
        <span className="text-base leading-none">💡</span>
        <div>
          <div className="font-semibold">
            {(ticket.kb_suggestions || []).length} KB suggestion{(ticket.kb_suggestions || []).length === 1 ? "" : "s"}{" "}
            {ticket.kb_suggestions_reranked_at ? "re-ranked after classification" : "matched at intake"}
          </div>
          <div className="text-[11px] text-sky-700 mt-0.5">
            {ticket.kb_suggestions_reranked_at
              ? "Refreshed when the agent set taxonomy. KB analytics tracks both the intake and re-rank events."
              : "Auto-attached when the ticket was created. Inserted articles are tracked in the KB analytics dashboard."}
          </div>
        </div>
      </div>
    )}

    <KbSuggesterPanel ticketId={ticket.id} onInsertLink={onInsertKbLink} />
  </div>
);
